<script lang="ts">
	//Design system components
	import { beforeUpdate, afterUpdate, onDestroy, createEventDispatcher, onMount } from 'svelte';
	import {
		toast,
		Modal,
		CircleAvatar,
		ImageUpload,
		ImageGroup,
		Alert,
		Container,
		Text,
		Button,
	} from '@Shelly/shelly-design-system';
	import type {
		ImageUploadPropsTypes,
		AlertPropsTypes,
		ImageGroupImageType,
	} from '@Shelly/shelly-design-system';
	import {
		getImages,
		mapImgArray,
		uploadImage,
		// saveCustomIconForDevice,
		// loadCustomIconsForDevice,
		// getImagesByType,
	} from './list-helpers';
	import { format } from 'svelte-i18n';
	import { classMap } from '@Shelly/shelly-design-system/helpers/component-utils';
	import { postReq } from 'src/services/http';
	import { user_data } from 'src/stores/account/user';
	import { capFirstChar } from 'src/helpers/utilities';
	import IconSearchModal from './IconSearchModal.svelte';
	import { AvatarColorVariant } from '@Shelly/shelly-design-system/helpers/enums';
	import { showNewLayout } from 'src/stores/toDelete/new_layout';

	const dispatch = createEventDispatcher();

	export let requestType = 'room';
	export let selectedImage = '';
	export let selectedBackgroundColor = '';
	export let selectedIcon = '';
	export let showColors = true;
	export let uploadedImagesDB = [];
	export let imagesByTypeDB = [];
	export let setDefaultImage = false;
	export let groupTitle: {
		upload?: string;
		type?: string;
		other?: string;
	} = {
		upload: $format('title_uploaded_pictures', {
			default: 'Uploaded pictures',
		}),
		type: $format('title_default_pictures', { default: 'Default pictures' }),
	};
	export let iconsDB = [];

	export let uploadSettings: {
		single: boolean;
		imageUploadSettings?: {
			type: ImageUploadPropsTypes['type'];
			btnSettings: ImageUploadPropsTypes['btnSettings'];
		};
	} = {
		single: false,
		imageUploadSettings: {
			type: 'circle',
			btnSettings: null,
		},
	};

	export let updateTypeImages = false;
	export let uploadImageAlert: {
		heading: AlertPropsTypes['heading'];
		type: AlertPropsTypes['type'];
	} = null;

	export let defaultType: ImageUploadPropsTypes['type'] = 'circle';
	export let imgGroupType: ImageUploadPropsTypes['type'] = 'circle';

	// Prop to receive persisted custom icons
	export let customIcons = [];

	const imageGroupTypes = {
		imgType: 'imgType',
		uploaded: 'uploaded',
		other: 'other',
		color: 'color',
		icon: 'icon',
	};

	let uploadedImages = [];
	let imagesByType = [];
	let otherImages = [];
	let imgsSet = false;
	let defaultImageSet = false;
	let showModal = false;
	let showIconFolderModal = false;

	const colorValues = Object.values(AvatarColorVariant).slice(0, -5);
	const colorArr = Object.values(colorValues);

	let imgGroupColors: ImageGroupImageType[] = [...Array(colorArr.length).keys()].map((_, i) => {
		const currentColor = colorValues[i] as ImageGroupImageType['backgroundColor'];
		return {
			backgroundColor: currentColor,
			id: i,
			selected: false,
			type: 'color',
		};
	});

	onMount(() => {
		setBackgroundColors();
		// Initialize with custom icons if provided
		if (customIcons.length > 0) {
			mergeCustomIconsWithDefaults();
		}
	});

	onDestroy(() => {
		selectedImage = '';
		selectedBackgroundColor = '';
		selectedIcon = '';
	});

	$: if (imagesByTypeDB) {
		setDefaultRoomImage();
	}

	beforeUpdate(() => {
		if (!imgsSet) {
			setDefaultRoomImage();
			setImagesForImgGroups();
			imgsSet = true;
		}
	});

	afterUpdate(() => {
		if (setDefaultImage && defaultImageSet) {
			dispatchSelectImage();
			defaultImageSet = false;
		}
	});

	const dispatchSelectImage = () => {
		dispatch('select-image', {
			image: selectedImage,
			backgroundColor: selectedBackgroundColor,
			icon: selectedIcon,
			isIcon: !!selectedIcon,
		});
	};

	const setDefaultRoomImage = () => {
		if (setDefaultImage && !defaultImageSet) {
			selectedImage = imagesByTypeDB[0]?.filepath;
			defaultImageSet = true;
		}
	};

	const getAllImagesFromGroups = () => {
		return [...uploadedImages, ...imagesByType, ...otherImages, ...imgGroupColors, ...iconsDB];
	};

	const setUploadedImages = () => {
		uploadedImages = mapImgArray(uploadedImagesDB, imageGroupTypes.uploaded, selectedImage);
	};

	// Merge custom icons with default images
	const mergeCustomIconsWithDefaults = () => {
		const formattedCustomIcons = customIcons.map((iconClass, index) => ({
			id: `custom-icon-${index}`,
			icon: iconClass,
			isIcon: true,
			selected: selectedIcon === iconClass,
			type: imageGroupTypes.imgType,
			local: false,
		}));

		// add the custom icons
		imagesByType = [...imagesByType, ...formattedCustomIcons];
	};

	const setImagesByType = () => {
		// Process all images including icons
		imagesByType = mapImgArray(
			imagesByTypeDB,
			imageGroupTypes.imgType,
			selectedImage,
			selectedIcon,
		).sort((a, b) => a.isIcon - b.isIcon);

		if (customIcons.length > 0) {
			mergeCustomIconsWithDefaults();
		}
	};

	const setImagesForImgGroups = () => {
		setImagesByType();
		setUploadedImages();
	};

	const setBackgroundColors = () => {
		let selectedIndex = -1;

		let mappedArray = imgGroupColors.map((color, index) => {
			const isSelected = color.backgroundColor === selectedBackgroundColor;

			if (isSelected) selectedIndex = index;

			return {
				...color,
				selected: isSelected,
			};
		});

		const selectedColor = mappedArray[selectedIndex];
		if (selectedColor) {
			mappedArray.splice(selectedIndex, 1);
			imgGroupColors = [selectedColor, ...mappedArray];
		}
	};

	const filterAllImagesByGroup = (allImages) => {
		uploadedImages = allImages.filter((img) => img.type === imageGroupTypes.uploaded);
		imagesByType = allImages.filter((img) => img.type === imageGroupTypes.imgType);
		otherImages = allImages.filter((img) => img.type === imageGroupTypes.other);
		imgGroupColors = allImages.filter((img) => img.type === imageGroupTypes.color);
		iconsDB = allImages.filter((img) => img.type === imageGroupTypes.icon);
	};

	const closeModal = () => {
		showModal = false;
	};

	const showDeleteModal = () => {
		showModal = true;
	};

	const removeActiveImage = () => {
		const allImages = getAllImagesFromGroups().map((img) => {
			return {
				...img,
				selected: false,
			};
		});

		filterAllImagesByGroup(allImages);
	};

	const markSelectedIcon = (iconClass: string) => {
		const allImages = getAllImagesFromGroups();
		const updatedImages = allImages.map((img) => ({
			...img,
			selected: img.icon === iconClass && img.isIcon,
		}));
		filterAllImagesByGroup(updatedImages);
	};

	let iconModalOpenedFromIcons = false;

	const openIconFolderModal = (fromIconsGroup = false) => {
		iconModalOpenedFromIcons = fromIconsGroup;
		showIconFolderModal = true;
	};

	const closeIconFolderModal = () => {
		showIconFolderModal = false;
	};

	// Handle icon selection from modal
	const onIconSelected = (event) => {
		const iconClass = event.detail.icon;

		// Clear all current selections
		removeActiveImage();

		// Set the new selections
		selectedIcon = iconClass;
		selectedImage = '';
		selectedBackgroundColor = '';

		// Check if icon already exists in the list
		const existingIcon = imagesByType.find((img) => img.icon === iconClass && img.isIcon);

		if (existingIcon) {
			// Icon already exists, just select it
			markSelectedIcon(iconClass);
		} else {
			// A new custom icon - so we add it temporarily to the list
			const newIcon = {
				id: `custom-icon-new`,
				icon: iconClass,
				isIcon: true,
				selected: true,
				type: iconModalOpenedFromIcons ? imageGroupTypes.icon : imageGroupTypes.imgType,
				local: false,
				isCustom: true,
			};

			if (iconModalOpenedFromIcons) {
				iconsDB = [newIcon, ...iconsDB];
			} else {
				imagesByType = [newIcon, ...imagesByType];
			}
		}

		// update the selection state across all groups
		const allImages = getAllImagesFromGroups();
		const updatedImages = allImages.map((img) => ({
			...img,
			selected: img.icon === iconClass && img.isIcon,
		}));

		// Update all image groups with the new selection
		filterAllImagesByGroup(updatedImages);

		// Dispatch the selection to parent
		dispatchSelectImage();

		// Dispatch custom icon event for parent to handle
		dispatch('custom-icon-selected', {
			icon: iconClass,
		});
	};

	// Handle image/icon/color selection from ImageGroups
	const onSelectImg = (event) => {
		const { detail } = event;

		// Clear all selections first
		removeActiveImage();

		if (detail.backgroundColor) {
			// User selected a background color
			selectedBackgroundColor = detail.backgroundColor;
			selectedImage = '';
			selectedIcon = '';
		} else if (detail.icon) {
			// User selected an icon
			selectedIcon = detail.icon;
			selectedImage = '';
			selectedBackgroundColor = '';
		} else if (detail.image || detail.filepath) {
			// User selected an image
			selectedImage = detail.filepath;
			selectedBackgroundColor = '';
			selectedIcon = '';
		}

		// Update the visual selection state
		const allImages = getAllImagesFromGroups();
		const updatedImages = allImages.map((img) => ({
			...img,
			selected: !!(
				(detail.backgroundColor && img.backgroundColor === detail.backgroundColor) ||
				(detail.icon && img.icon === detail.icon && img.isIcon) ||
				((detail.image || detail.filepath) &&
					(img.image === (detail.image || detail.filepath) ||
						img.filepath === (detail.image || detail.filepath)))
			),
		}));

		filterAllImagesByGroup(updatedImages);

		dispatchSelectImage();
	};

	const onUploadImage = async (event) => {
		const result = await uploadImage(event, requestType);
		if (Array.isArray(result)) {
			uploadedImagesDB = result;
			toast.open({
				type: 'success',
				title: $format('toast_title_picture_uploaded', {
					default: 'Picture uploaded successfully.',
				}),
			});

			removeActiveImage();

			selectedImage = uploadedImagesDB[0].filepath;
			uploadedImages = mapImgArray(uploadedImagesDB, imageGroupTypes.uploaded, selectedImage);

			// to be able to upload same img after that
			event.target.value = '';

			dispatch('upload-image', {
				image: selectedImage,
				uploadedImagesDB: uploadedImagesDB,
			});
		}
	};

	const onDeleteImage = async () => {
		const imgToDelete = uploadedImages[0];

		const res = await postReq('/user_files/delete_image', {
			img_path: imgToDelete.filepath.replace('assets/user_images/', '').replace('thumb_', ''),
		});

		if (res.isok) {
			toast.open({
				type: 'success',
				title: $format('toast_title_picture_deleted', {
					default: 'Picture deleted successfully.',
				}),
			});

			uploadedImages = uploadedImages.filter((img) => img.filepath !== imgToDelete.filepath);
			uploadedImagesDB = await getImages(requestType);

			if (selectedImage === imgToDelete.filepath) {
				selectedImage = imagesByType[0].filepath;
				const imagesFromGroup = getAllImagesFromGroups();
				const selectedImg0bj = imagesFromGroup.find((img) => img.filepath === selectedImage);
				const allImages = imagesFromGroup.map((img) => {
					if (selectedImg0bj.id === img.id && img.type === selectedImg0bj.type) {
						selectedImage = img.filepath;
						selectedBackgroundColor = '';

						return {
							...img,
							selected: true,
						};
					}

					return {
						...img,
						selected: false,
					};
				});

				filterAllImagesByGroup(allImages);
			}

			dispatch('delete-image', {
				image: selectedImage,
				uploadedImagesDB: uploadedImagesDB,
			});
		}
	};

	const deleteBtnHandler = () => {
		closeModal();
		onDeleteImage();
	};

	$: (customIcons, mergeCustomIconsWithDefaults());
	$: (uploadedImagesDB, setUploadedImages());
	$: (imagesByTypeDB || updateTypeImages, setImagesByType());
	$: isAlert = uploadImageAlert && uploadImageAlert.heading;

	// ensure UI updates when selectedIcon changes
	$: if (selectedIcon) {
		const allImages = getAllImagesFromGroups();
		const existsInAnyGroup = allImages.some((img) => img.icon === selectedIcon && img.isIcon);

		const baseImages = existsInAnyGroup
			? allImages
			: [
					{
						id: `restored-icon`,
						icon: selectedIcon,
						isIcon: true,
						selected: true,
						type: imageGroupTypes.icon,
					},
					...allImages,
				];

		filterAllImagesByGroup(
			baseImages.map((img) => ({
				...img,
				selected: img.icon === selectedIcon && img.isIcon,
			})),
		);
	}
</script>

<ImageGroup
	class={classMap({ 'mb-l': !isAlert, 'mb-s': isAlert })}
	title={groupTitle.upload}
	images={uploadedImages}
	single={uploadSettings.single}
	on:click={onSelectImg}
>
	<ImageUpload
		accept=".jpg, .jpeg, .heif, .png"
		slot="imgUpload"
		on:change={onUploadImage}
		on:delete={showDeleteModal}
		{...uploadSettings.imageUploadSettings}
		type={uploadedImages.length > 0 ? defaultType : imgGroupType}
		btnSettings={{
			title: $format('button_title_picture_upload', {
				default: 'Upload picture',
			}),
			variant: 'primary',
			icon: 'fa-solid fa-upload',
			editTypePrimaryBtnIcon: 'fa-solid fa-arrows-rotate',
			editTypePrimaryBtnTitle: $format('button_title_picture_change', {
				default: 'Change picture',
			}),
			deleteTypePrimaryBtnIcon: 'fa-solid fa-trash',
			deleteTypePrimaryBtnTitle: $format('button_title_picture_delete', {
				default: 'Delete picture',
			}),
		}}
	/>
</ImageGroup>

{#if isAlert}
	<Alert class="mt-s mb-l" heading={uploadImageAlert.heading} type={uploadImageAlert.type} />
{/if}

{#if requestType === 'dashboard' && $showNewLayout}
	<ImageGroup
		class="mb-l text-capitalize"
		title={$format('icons', { default: 'icons' })}
		images={iconsDB}
		on:click={onSelectImg}
	>
		<ImageUpload
			slot="imgUpload"
			type="circle"
			on:click={(e) => {
				e.preventDefault();
				openIconFolderModal(true);
			}}
		/>
	</ImageGroup>
{:else}
	<ImageGroup
		class="mb-l text-capitalize"
		title={$format('icons', { default: 'icons' })}
		images={iconsDB}
		on:click={onSelectImg}
	/>
{/if}

{#if requestType === 'device'}
	<ImageGroup
		class="image-type-title text-capitalize"
		title={groupTitle.type}
		images={imagesByType}
		on:click={onSelectImg}
	>
		<ImageUpload
			slot="imgUpload"
			type="circle"
			on:click={(e) => {
				e.preventDefault();
				openIconFolderModal();
			}}
		/>
	</ImageGroup>
{:else}
	<ImageGroup
		class="image-type-title text-capitalize"
		title={groupTitle.type}
		images={imagesByType}
		on:click={onSelectImg}
	/>
{/if}

<ImageGroup
	class="mt-l"
	title={$format('appliance_type_other', {
		default: 'Other',
	})}
	images={otherImages}
	on:click={onSelectImg}
/>

{#if showColors}
	<ImageGroup
		class="mt-l"
		title={$format('image_group_title_colors_gradients', {
			default: 'Colors and Gradients.',
		})}
		images={imgGroupColors}
		on:click={onSelectImg}
	/>
{/if}

<Modal
	{showModal}
	title={`${capFirstChar(
		$format('button_delete', { default: 'Delete' }),
	)} ${capFirstChar($format('process_image', { default: 'Image' }))}?`}
	on:close={closeModal}
	class="justify-content-center"
>
	<svelte:fragment slot="content">
		<Container padding="0">
			<div class="d-flex justify-content-center mb-m">
				<CircleAvatar
					image={$user_data.user_api_url + '/shelly_files/' + uploadedImages[0].filepath}
					size="xl"
				/>
			</div>
			<Text>
				{$format('delete_img_msg', {
					default:
						"Deleting this image will permanently remove it from your account. Once deleted, you won't be able to find it in your uploaded pictures.",
				})}
			</Text>
		</Container>
	</svelte:fragment>
	<svelte:fragment slot="footer">
		<div class="d-flex justify-content-end">
			<Button
				variant="secondary"
				on:click={closeModal}
				class="min-width-md-m min-width-sm-auto width-md-auto width-sm-full text-nowrap mr-s"
				responsiveSize={{
					sm: 'm',
				}}
			>
				{$format('button_cancel', { default: 'Cancel' })}
			</Button>
			<Button
				aria-label={$format('aria_delete_image', { default: 'Delete image' })}
				on:click={deleteBtnHandler}
				class="min-width-md-m min-width-sm-auto width-md-auto width-sm-full text-nowrap"
				variant="danger"
				responsiveSize={{
					sm: 'm',
				}}
			>
				{capFirstChar($format('button_delete', { default: 'Delete' }))}
				{$format('process_image', { default: 'Image' })}
			</Button>
		</div>
	</svelte:fragment>
</Modal>

<IconSearchModal
	showModal={showIconFolderModal}
	on:close={closeIconFolderModal}
	on:icon-selected={onIconSelected}
/>
